package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.CountUp
import co.maytes.standupbot.dashboard.components.motion.prefersReducedMotion
import co.maytes.standupbot.dashboard.components.widgets.AnimatedCheck
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Eyebrow
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertTriangle
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideFileText
import co.maytes.standupbot.dashboard.components.widgets.icons.LucidePlay
import co.maytes.standupbot.dashboard.components.widgets.icons.LucidePlug
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgCircle
import co.maytes.standupbot.dashboard.features.setup.SetupCheckReportView
import co.maytes.standupbot.dashboard.features.setup.SetupGlyph
import co.maytes.standupbot.dashboard.features.setup.TestMySetupButton
import co.maytes.standupbot.dashboard.styles.BrandHue
import co.maytes.standupbot.dashboard.styles.HomeArt
import co.maytes.standupbot.dashboard.styles.HomeClasses
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import com.varabyte.kobweb.compose.ui.Modifier
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.Text
import kotlin.math.PI

private const val PERCENT = 100.0
private const val HALF = 2
private const val STEP_ICON = 12

@Composable
fun HomeSetupView(journey: SetupJourney, baseIndex: Int) {
    HmDiv(HomeClasses.PANEL, HomeClasses.SETUP, modifier = Modifier.staggerIn(baseIndex)) {
        HmDiv(HomeClasses.SETUP_SIDE) {
            Eyebrow(text = "Setup", withDot = true)
            ProgressRing(journey.percent)
            HmStrong(journey.progressLabel)
            NextAction(journey)
        }
        HmDiv(modifier = Modifier.stack(Spacing.md)) {
            HmDiv(HomeClasses.MILESTONES) {
                journey.milestones.forEachIndexed { index, milestone ->
                    MilestoneCard(milestone, active = journey.isNextMilestone(milestone), index = baseIndex + index + 1)
                }
            }
            SetupCheckReportView()
        }
    }
}

@Composable
private fun NextAction(journey: SetupJourney) {
    val next = journey.next ?: return
    val action = next.action
    HmMuted("Next: ${next.label}")
    val nav = rememberNavigator()
    if (action != null) {
        Button(label = action.label, variant = ButtonVariant.Primary, onClick = { nav(action.path) })
        TestMySetupButton()
    } else {
        TestMySetupButton(variant = ButtonVariant.Primary, size = ButtonSize.Md)
    }
}

@Composable
private fun ProgressRing(percent: Int) {
    val size = HomeArt.RING_SIZE
    val radius = HomeArt.RING_RADIUS
    val circumference = 2 * PI * radius
    var settled by remember { mutableStateOf(false) }
    LaunchedEffect(percent) {
        if (!prefersReducedMotion()) delay(HomeArt.RING_SETTLE_MS)
        settled = true
    }
    val offset = circumference * (1 - (if (settled) percent else 0) / PERCENT)
    HmDiv(HomeClasses.RING) {
        Svg(attrs = {
            attr("viewBox", "0 0 $size $size")
            attr("width", size.toString())
            attr("height", size.toString())
            attr("aria-hidden", "true")
        }) {
            SvgCircle(attrs = {
                classes(HomeClasses.RING_TRACK)
                attr("cx", (size / HALF).toString())
                attr("cy", (size / HALF).toString())
                attr("r", radius.toString())
                attr("stroke-width", HomeArt.RING_STROKE.toString())
            })
            SvgCircle(attrs = {
                classes(HomeClasses.RING_FILL)
                attr("cx", (size / HALF).toString())
                attr("cy", (size / HALF).toString())
                attr("r", radius.toString())
                attr("stroke-width", HomeArt.RING_STROKE.toString())
                attr("stroke-dasharray", circumference.toString())
                attr("style", "stroke-dashoffset: $offset")
            })
        }
        HmDiv(HomeClasses.RING_LABEL) { CountUp(value = percent, format = { "$it%" }) }
    }
}

@Composable
private fun MilestoneCard(milestone: Milestone, active: Boolean, index: Int) {
    val nav = rememberNavigator()
    HmDiv(
        *classesOf(HomeClasses.MILESTONE, if (active) HomeClasses.MILESTONE_ACTIVE else null),
        modifier = Modifier.tinted(tintFor(milestone.id)).staggerIn(index),
    ) {
        HmCardHead(
            title = milestone.id.title,
            tint = tintFor(milestone.id),
            icon = { MilestoneIcon(milestone.id) },
            trailing = { HmMuted(milestone.countLabel) },
        )
        milestone.steps.forEach { step -> StepRow(step, onAction = nav) }
    }
}

@Composable
private fun StepRow(step: JourneyStep, onAction: (String) -> Unit) {
    HmDiv(HomeClasses.STEP) {
        StepMark(step.glyph)
        HmDiv(HomeClasses.STEP_TEXT) {
            HmStrong(step.label)
            if (step.glyph == SetupGlyph.Broken || step.glyph == SetupGlyph.Warning) {
                step.detail?.let { HmMuted(it) }
            }
        }
        val action = step.action
        if (action != null && step.glyph != SetupGlyph.Done) {
            HmLink(action.path, HomeClasses.CONNECT) { Text(action.label) }
        }
    }
}

@Composable
private fun StepMark(glyph: SetupGlyph) {
    val cls = when (glyph) {
        SetupGlyph.Done -> HomeClasses.MARK_DONE
        SetupGlyph.Todo -> HomeClasses.MARK_TODO
        SetupGlyph.Optional -> HomeClasses.MARK_OPTIONAL
        SetupGlyph.Broken -> HomeClasses.MARK_BAD
        SetupGlyph.Warning -> HomeClasses.MARK_WARN
    }
    key(glyph) {
        HmDiv(HomeClasses.MARK, cls) {
            when (glyph) {
                SetupGlyph.Done -> AnimatedCheck(sizePx = STEP_ICON)
                SetupGlyph.Broken -> LucideX(size = STEP_ICON)
                SetupGlyph.Warning -> LucideAlertTriangle(size = STEP_ICON)
                SetupGlyph.Todo, SetupGlyph.Optional -> Unit
            }
        }
    }
}

@Composable
private fun MilestoneIcon(id: MilestoneId) {
    when (id) {
        MilestoneId.Connect -> LucidePlug(size = HomeArt.ICON_PX)
        MilestoneId.Team -> LucideUsers(size = HomeArt.ICON_PX)
        MilestoneId.Context -> LucideFileText(size = HomeArt.ICON_PX)
        MilestoneId.Launch -> LucidePlay(size = HomeArt.ICON_PX)
    }
}

private fun tintFor(id: MilestoneId): String =
    when (id) {
        MilestoneId.Connect -> BrandHue.LINEAR
        MilestoneId.Team -> BrandHue.SLACK
        MilestoneId.Context -> BrandHue.CLAUDE
        MilestoneId.Launch -> Palette.accent.cssRgb()
    }

@Composable
fun HomeSetupStrip(baseIndex: Int, remaining: JourneyStep? = null, remainingCount: Int = 0) {
    HmDiv(HomeClasses.PANEL, HomeClasses.STRIP, modifier = Modifier.staggerIn(baseIndex)) {
        if (remaining == null) {
            HmDiv(HomeClasses.MARK, HomeClasses.MARK_DONE) { LucideCheck(size = STEP_ICON) }
            HmStrong("Setup complete")
        } else {
            HmDiv(HomeClasses.MARK, HomeClasses.MARK_TODO) {}
            HmStrong(if (remainingCount == 1) "1 setup step left" else "$remainingCount setup steps left")
            HmMuted(remaining.label)
        }
        HmDiv(HomeClasses.STRIP_END) { TestMySetupButton() }
    }
    SetupCheckReportView()
}
